import { useEffect, useRef, useState } from 'react';
import { Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Content,
  EmptyState,
  InfoCard,
  Progress,
  WarningPanel,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { ReleaseList } from './ReleaseList';
import { formatRelativeDate } from './formatRelativeDate';
import {
  SystemReleasesContent,
  systemComponentRefs,
} from './SystemReleasesContent';
import {
  releaseErrorMessage,
  releasesApiRef,
  type RepoReleasesResponse,
} from './api';

const GITHUB_PROJECT_SLUG = 'github.com/project-slug';

/** How many releases the tab shows, and how many "Show more" adds. */
const RELEASE_PAGE_SIZE = 5;

const useStyles = makeStyles({
  cardActions: {
    justifyContent: 'center',
  },
});

/** Kinds that never get a Releases tab; the Releases page filters by owner. */
const KINDS_WITHOUT_TAB = new Set(['group', 'domain']);

type ReleasesEntity = Parameters<typeof systemComponentRefs>[0];

/** Whether this entity's tab merges the releases of the system's components. */
function isSystemRollup(entity: ReleasesEntity): boolean {
  return (
    entity.kind?.toLowerCase() === 'system' &&
    systemComponentRefs(entity).length > 0
  );
}

/**
 * Whether an entity's page gets a Releases tab: it points at a GitHub
 * repository, or it is a system with components, whose repos may have them.
 *
 * Groups and domains never do, even with a repo annotation: a team's repo is
 * usually its docs, and the Releases page already filters by owner.
 *
 * Deliberately not a test for whether releases exist — that needs a fetch,
 * and a tab that appears only after one would flicker in and out.
 */
export function isReleasesAvailable(entity: ReleasesEntity): boolean {
  if (KINDS_WITHOUT_TAB.has(entity?.kind?.toLowerCase() ?? '')) {
    return false;
  }
  return (
    Boolean(entity?.metadata?.annotations?.[GITHUB_PROJECT_SLUG]) ||
    isSystemRollup(entity)
  );
}

/**
 * One repository's releases: the most recent releases of the repo
 * named by its `github.com/project-slug` annotation.
 *
 * Reads the per-repo backend route rather than filtering the org-wide feed.
 * That feed caps each repo and then truncates globally by date, so a repo that
 * has not shipped lately can fall out of it and look release-less.
 *
 * Several entities may share one repo — a system and its components often do.
 * They all show the same releases on purpose: hunting for the one entity that
 * happens to carry them would be worse.
 */
const RepoReleasesContent = ({
  projectSlug,
}: {
  projectSlug: string | undefined;
}) => {
  const classes = useStyles();
  const releasesApi = useApi(releasesApiRef);

  // State belongs to one repository: EntityReleasesContent keys this
  // component by slug, so moving to another repo starts from scratch.
  const [data, setData] = useState<RepoReleasesResponse | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();
  const [limit, setLimit] = useState(RELEASE_PAGE_SIZE);
  const listRef = useRef<HTMLDivElement>(null);
  /** How many releases were on screen when "Show more" was pressed. */
  const shownBeforeMore = useRef<number | undefined>();

  useEffect(() => {
    let cancelled = false;

    async function fetchReleases() {
      if (!projectSlug) {
        setLoading(false);
        return;
      }

      // Exactly two non-empty parts: "a/b/c" or "a//b" would otherwise be
      // read as a/b, or encoded into a path the route rejects.
      const parts = projectSlug.split('/');
      if (parts.length !== 2 || parts.some(part => !part)) {
        setLoading(false);
        setError(
          new Error(
            `The github.com/project-slug annotation "${projectSlug}" is not in owner/repo form.`,
          ),
        );
        return;
      }
      const [owner, repo] = parts;

      setLoading(true);
      setError(undefined);

      try {
        const result = await releasesApi.getRepoReleases(
          { owner, repo },
          { limit },
        );
        if (!cancelled) {
          setData(result);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchReleases();
    return () => {
      cancelled = true;
    };
  }, [projectSlug, limit, releasesApi]);

  const age = data ? formatRelativeDate(data.fetchedAt) : '';
  const canShowMore = Boolean(
    data && data.releases.length < data.totalCount && limit < data.maxLimit,
  );

  // After "Show more" loads, focus stays on the button when it is still
  // there. When it is gone (everything is on screen), it is moved to the first
  // newly shown release, so keyboard focus is never dropped to the page.
  useEffect(() => {
    const before = shownBeforeMore.current;
    if (loading || before === undefined) return;
    shownBeforeMore.current = undefined;
    if (canShowMore) return;
    const row = listRef.current?.querySelectorAll<HTMLElement>(
      '[data-release-item]',
    )[before];
    row
      ?.querySelector<HTMLElement>('[role="button"], a[href], button')
      ?.focus();
  }, [loading, canShowMore]);

  const showMore = () => {
    // aria-disabled rather than disabled, so the focused button keeps focus.
    if (loading || !data) return;
    shownBeforeMore.current = data.releases.length;
    setLimit(current => current + RELEASE_PAGE_SIZE);
  };

  return (
    <Content>
      {loading && !data && <Progress />}

      {/* A failed "Show more" keeps what is already on screen below. */}
      {!loading && error && (
        <WarningPanel
          title="Could not load releases"
          message={releaseErrorMessage(error)}
        />
      )}

      {!loading && !error && data && data.releases.length === 0 && (
        <EmptyState
          missing="data"
          title="No releases yet"
          description={`${projectSlug} has not published any GitHub releases.`}
        />
      )}

      {/* Stays mounted while more releases load, so the focused button and
          any expanded notes survive pagination. */}
      {data && data.releases.length > 0 && (
        <InfoCard
          title="Recent releases"
          subheader={
            <span title={new Date(data.fetchedAt).toLocaleString()}>
              {age ? `Updated ${age}` : 'Updated just now'}
              {data.totalCount > data.releases.length
                ? ` · showing ${data.releases.length} of ${data.totalCount}`
                : ''}
            </span>
          }
          actionsClassName={classes.cardActions}
          actions={
            canShowMore ? (
              <Button
                color="primary"
                aria-disabled={loading}
                onClick={showMore}
              >
                {loading
                  ? 'Loading…'
                  : `Show ${Math.min(
                      RELEASE_PAGE_SIZE,
                      data.totalCount - data.releases.length,
                    )} more`}
              </Button>
            ) : undefined
          }
        >
          <div ref={listRef} aria-busy={loading}>
            <ReleaseList releases={data.releases} />
          </div>
        </InfoCard>
      )}
    </Content>
  );
};

/**
 * The Releases tab for a catalog entity. A system with components shows the
 * merged releases of its own repo and theirs; any other entity shows its own
 * repo's.
 */
export const EntityReleasesContent = () => {
  const { entity } = useEntity();
  if (isSystemRollup(entity)) return <SystemReleasesContent />;
  const projectSlug = entity.metadata.annotations?.[GITHUB_PROJECT_SLUG];
  // Keyed by repository: another entity's repo never shows this one's
  // releases, page count or error while it loads.
  return <RepoReleasesContent key={projectSlug} projectSlug={projectSlug} />;
};

export default EntityReleasesContent;
